import React, { useState, useEffect, useRef } from 'react';
import { Search, Send, Paperclip, MoreHorizontal, Star, Archive, Filter, ChevronDown, Building, MapPin, Clock, CheckCheck, Loader } from 'lucide-react';
import { io } from 'socket.io-client';
import api from '../../../utils/Api';
import { useLocation } from 'react-router-dom';

const ENDPOINT = process.env.REACT_APP_HOST_API || 'http://localhost:4000'; // Adjust if needed

const ProfileAvatar = ({ user, size = "small" }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let objectUrl = null;

    const fetchImage = async () => {
      if (!user.profile_picture) return;

      if (user.profile_picture.startsWith('http')) {
        setImageUrl(user.profile_picture);
        return;
      }

      try {
        setLoading(true);
        const pathParts = user.profile_picture.split('/');
        const filename = pathParts.pop();
        const folder = pathParts.length > 0 ? pathParts.join('/') : 'profile_pictures';

        const response = await api.get(`/files/${folder}/${filename}`, {
          responseType: 'blob',
        });

        const blob = new Blob([response.data], { type: response.headers['content-type'] });
        objectUrl = window.URL.createObjectURL(blob);
        setImageUrl(objectUrl);
      } catch (error) {
        console.error("Error loading profile picture:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchImage();

    return () => {
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [user.profile_picture]);

  const dimensions = size === "large" ? "w-12 h-12" : "w-10 h-10";
  const fontSize = size === "large" ? "text-lg" : "text-sm";

  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt={user.first_name}
        className={`${dimensions} rounded-full object-cover flex-shrink-0`}
      />
    );
  }

  return (
    <div className={`${dimensions} rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0`}>
      <span className={`${fontSize} font-bold text-primary-600`}>
        {user.first_name?.[0]}{user.last_name?.[0]}
      </span>
    </div>
  );
};

export default function RecruiterMessaging() {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [socket, setSocket] = useState(null);
  const messagesEndRef = useRef(null);
  const location = useLocation();

  // Initialize Socket.io
  useEffect(() => {
    const newSocket = io(ENDPOINT);
    setSocket(newSocket);

    return () => newSocket.close();
  }, []);

  // Fetch conversations on mount
  useEffect(() => {
    fetchConversations();
  }, []);

  const hasInitialSelectionBeenMade = useRef(false);

  // Handle initial conversation selection from navigation state (e.g. from CVthèque)
  useEffect(() => {
    if (conversations.length > 0 && !hasInitialSelectionBeenMade.current) {
      if (location.state?.conversationId) {
        const conv = conversations.find(c => c._id === location.state.conversationId);
        if (conv) {
          setSelectedConversation(conv);
          hasInitialSelectionBeenMade.current = true;
        }
      } else if (!selectedConversation && window.innerWidth >= 768) {
        // Select first conversation by default if none selected AND we are on desktop
        setSelectedConversation(conversations[0]);
        hasInitialSelectionBeenMade.current = true;
      }
    }
  }, [conversations, location.state, selectedConversation]);

  // Track selected conversation ID for async operations
  const selectedConversationIdRef = useRef(null);

  // Fetch messages when conversation changes
  useEffect(() => {
    if (selectedConversation) {
      selectedConversationIdRef.current = selectedConversation._id;
      fetchMessages(selectedConversation._id);
      if (socket) {
        socket.emit('join_conversation', selectedConversation._id);
      }
    } else {
      selectedConversationIdRef.current = null;
    }
  }, [selectedConversation, socket]);

  // Listen for incoming messages
  useEffect(() => {
    if (!socket) return;

    socket.on('receive_message', (message) => {
      if (selectedConversation && message.conversation_id === selectedConversation._id) {
        setMessages((prev) => {
          if (prev.some(m => m._id === message._id)) return prev;
          return [...prev, message];
        });
        scrollToBottom();
      }
      // Update last message in conversation list
      setConversations((prev) => prev.map(c => {
        if (c._id === message.conversation_id) {
          return {
            ...c,
            last_message: {
              ...c.last_message,
              content: message.content,
              timestamp: message.timestamp,
              read: false // Or true if we are in the conversation
            },
            updated_at: new Date()
          };
        }
        return c;
      }).sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)));
    });

    return () => socket.off('receive_message');
  }, [socket, selectedConversation]);

  const fetchConversations = async () => {
    try {
      const response = await api.get('/chat/conversations');
      setConversations(response.data.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching conversations:", error);
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    setLoadingMessages(true);
    try {
      const response = await api.get(`/chat/messages/${conversationId}`);
      setMessages(response.data.data);
      scrollToBottom();
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedConversation) return;

    const content = messageText;
    setMessageText('');

    // Optimistic update
    const currentUser = JSON.parse(localStorage.getItem('user'));
    const tempMessage = {
      _id: `temp-${Date.now()}`,
      content: content,
      sender: { _id: currentUser?.id },
      timestamp: new Date().toISOString(),
      isOptimistic: true
    };

    if (selectedConversationIdRef.current === selectedConversation._id) {
      setMessages((prev) => {
        if (prev.some(m => m._id === tempMessage._id)) return prev;
        return [...prev, tempMessage];
      });
      scrollToBottom();
    }

    try {
      const response = await api.post('/chat/messages', {
        conversationId: selectedConversation._id,
        content
      });

      const newMessage = response.data.data;

      // Emit to socket
      socket.emit('send_message', newMessage);

      // Replace optimistic message with real message
      if (selectedConversationIdRef.current === selectedConversation._id) {
        setMessages((prev) => prev.map(m => m._id === tempMessage._id ? newMessage : m));
      }


      // Update conversation list
      setConversations((prev) => prev.map(c => {
        if (c._id === selectedConversation._id) {
          return {
            ...c,
            last_message: {
              ...c.last_message,
              content: content,
              timestamp: new Date(),
              read: true
            },
            updated_at: new Date()
          };
        }
        return c;
      }).sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)));

    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const getOtherParticipant = (conversation) => {
    // Assuming current user is recruiter, we want to find the candidate
    // But logic should be generic: find participant that is NOT me.
    // We don't have my ID easily here without context or decoding token.
    // However, usually the backend populates participants.
    // Let's assume the backend returns participants array.
    // Since this is Recruiter app, the other one is likely a Candidate or User with role 'candidate'.
    // A simple heuristic: if I am logged in, I have my token.
    // Better: backend should maybe return `otherParticipant` field?
    // Or we filter by role if we know our role.

    // For now, let's take the first participant that doesn't look like me?
    // Or just take the one that is NOT the current user ID.
    // We can get current user ID from local storage or context if available.
    const user = JSON.parse(localStorage.getItem('user'));
    if (!user || !conversation.participants) return {};
    return conversation.participants.find(p => p._id !== user.id) || conversation.participants[0];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader className="w-10 h-10 text-primary-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex bg-gray-50 dark:bg-gray-950" style={{ height: "calc(100vh - 4rem)" }}>

      {/* Sidebar */}
      {/* Sidebar */}
      <div className={`${selectedConversation ? 'hidden md:flex' : 'flex'} w-full md:w-80 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 flex-col`}>
        {/* Header */}
        <div className="p-4 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Messages</h1>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setFilterOpen(!filterOpen)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors relative"
              >
                <Filter className="w-4 h-4 text-gray-600" />
                <ChevronDown className="w-3 h-3 text-gray-600 absolute -bottom-1 -right-1" />
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent placeholder-gray-500 dark:placeholder-gray-400"
            />
          </div>
        </div>

        {/* Conversations list */}
        <div className="flex-1 overflow-y-auto">
          {conversations.map((conversation) => {
            const otherUser = getOtherParticipant(conversation);
            return (
              <div
                key={conversation._id}
                onClick={() => setSelectedConversation(conversation)}
                className={`p-4 border-b border-gray-100 dark:border-gray-800 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${selectedConversation?._id === conversation._id ? 'bg-primary-50 dark:bg-primary-900/20 border-r-2 border-r-primary-500' : ''
                  }`}
              >
                <div className="flex items-start space-x-3">
                  <div className="relative">
                    <ProfileAvatar user={otherUser} size="large" />
                    {/* Status indicator if available */}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <h3 className="font-semibold text-gray-900 dark:text-white truncate">{otherUser.first_name} {otherUser.last_name}</h3>
                      </div>
                      <span className="text-xs text-gray-500">
                        {conversation.last_message?.timestamp ? new Date(conversation.last_message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                      </span>
                    </div>

                    <div className="flex items-center justify-between mt-1">
                      <p className={`text-sm truncate pr-2 ${!conversation.last_message?.read && conversation.last_message?.sender !== JSON.parse(localStorage.getItem('user'))?.id ? 'font-medium text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'}`}>
                        {conversation.last_message?.content || 'Nouvelle conversation'}
                      </p>
                      {/* Unread indicator logic here */}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Main chat area */}
      <div className={`${!selectedConversation ? 'hidden md:flex' : 'flex'} flex-1 flex-col`}>
        {selectedConversation ? (
          <>
            {/* Chat header */}
            <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setSelectedConversation(null)}
                    className="md:hidden p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <ChevronDown className="w-5 h-5 text-gray-600 rotate-90" />
                  </button>
                  {/* Header Avatar */}
                  <ProfileAvatar user={getOtherParticipant(selectedConversation)} size="small" />
                  <div>
                    <h2 className="font-semibold text-gray-900 dark:text-white">
                      {getOtherParticipant(selectedConversation).first_name} {getOtherParticipant(selectedConversation).last_name}
                    </h2>
                  </div>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {loadingMessages ? (
                <div className="flex items-center justify-center h-full">
                  <Loader className="w-8 h-8 text-primary-600 animate-spin" />
                </div>
              ) : messages.map((message) => {
                const isMe = message.sender._id === JSON.parse(localStorage.getItem('user'))?.id || message.sender === JSON.parse(localStorage.getItem('user'))?.id;
                return (
                  <div
                    key={message._id}
                    className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-sm lg:max-w-md xl:max-w-lg ${isMe
                      ? 'bg-primary-600 text-white'
                      : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white'
                      } rounded-2xl px-4 py-3 shadow-sm`}>
                      <p className="text-sm leading-relaxed">{message.content}</p>
                      <div className={`flex items-center justify-end mt-2 space-x-1 ${isMe ? 'text-primary-100' : 'text-gray-400'
                        }`}>
                        <span className="text-xs">{new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        {isMe && (
                          <CheckCheck className="w-3 h-3" />
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Message input */}
            <div className="bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 p-4">
              <div className="flex items-center space-x-3">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    placeholder="Tapez votre message..."
                    className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-full focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  />
                </div>

                <button
                  onClick={handleSendMessage}
                  className="bg-primary-600 hover:bg-primary-700 text-white p-3 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!messageText.trim()}
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            Sélectionnez une conversation pour commencer
          </div>
        )}
      </div>
    </div>
  );
}